import { useGeolocation } from '@uidotdev/usehooks';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LocationType } from '../../../@posturinn/client-react';
import { fetchLocations } from '../../../actions/locationsActions';
import { getCurrentLangPretty } from '../../../helpers/url/urlHelpers';
import useLocations from '../../../hooks/useLocations';
import { ReactComponent as IconLocation } from '../../../icons/location.svg';
import ISettingsViewModel from '../../../interfaces/umbracoNodes/settingsViewModel';
import Button from '../../base/button';
import { Column, Columns } from '../../base/grid';
import { A } from '../../base/typography';
import Error from '../../error';
import Loading from '../../loading';
import { findClosestLocation } from './helpers';
import * as StyledPostBox from './location.styles';

interface Props {
	locationType: LocationType;
}

const createOpenStreetMapUrls = (latitude: number, longitude: number, label: string) => {
	const delta = 0.01;
	const bbox = [longitude - delta, latitude - delta, longitude + delta, latitude + delta].join('%2C');
	const marker = `${latitude}%2C${longitude}`;

	return {
		link: `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=16/${latitude}/${longitude}`,
		embed: `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`,
		title: label,
	};
};

const Location: FC<Props> = ({ locationType }) => {
	const lang = getCurrentLangPretty();
	const { t } = useTranslation('common');
	const dispatch = useDispatch<any>();
	const geolocation = useGeolocation();
	const [nearestLocation, setNearestLocations] = useState(null);
	const { settings }: { settings: ISettingsViewModel } = useSelector(({ pageReducer }) => pageReducer);
	const { locations, locationsError, locationsLoading } = useLocations();
	useEffect(() => {
		const { latitude, longitude } = geolocation;

		if (!isNaN(latitude) && !isNaN(longitude) && locations === null && !locationsError && !locationsLoading) {
			dispatch(fetchLocations());
		}
	}, [locations, locationsError, locationsLoading, dispatch, geolocation, lang]);

	useEffect(() => {
		if (nearestLocation === null && geolocation.longitude !== null && locations !== null) {
			setNearestLocations(findClosestLocation(geolocation, locations, locationType));
		}
	}, [nearestLocation, setNearestLocations, geolocation, locations, locationType]);

	if (locationsLoading || !locations) return <Loading />;

	if (locationsError) {
		let subText = `${t('error.error-postoffice')}`;
		if (locationType === LocationType.PostBox) subText = `${t('error.error-postbox')}`;
		if (locationType === LocationType.ParcelPoint) subText = `${t('error.error-parcelpoint')}`;
		return <Error heading={`${t('error.error-heading')}`} subText={subText} />;
	}

	// Should only happen on the first render where we haven't started fetching anything yet
	if (!nearestLocation) return null;

	const mapUrls = createOpenStreetMapUrls(
		Number(nearestLocation.latitude),
		Number(nearestLocation.longitude),
		`${nearestLocation.address.street1}, ${nearestLocation.address.postalCode} ${nearestLocation.address.city}`
	);

	const getHeadingText = locationType => {
		if (locationType === LocationType.PostBox) return t('post-box.post-box-near-you');
		if (locationType === LocationType.ParcelPoint) return t('parcel-point.parcel-point-near-you');
		return t('post-office.post-offices-near-you');
	};

	const getButtonText = locationType => {
		if (locationType === LocationType.PostBox) return t('post-box.see-all-post-boxes');
		if (locationType === LocationType.ParcelPoint) return t('parcel-point.see-all-parcel-points');
		return t('post-office.see-all-post-offices');
	};

	const getDestinationUrl = locationType => {
		let tab = 0;
		if (locationType === LocationType.PostBox) tab = 1;
		if (locationType === LocationType.ParcelPoint) tab = 2;
		return `${settings.Values.OpeningHours.Url}?tab=` + tab;
	};

	return (
		<StyledPostBox.Wrapper>
			<Columns>
				<Column tablet={4 / 12}>
					<StyledPostBox.LocationImage image={`/images/locations/${nearestLocation.id}.jpg`} />
				</Column>
				<Column tablet={4 / 12}>
					<StyledPostBox.InfoHeading marginBottom="2.5rem">
						{getHeadingText(nearestLocation.locationType)}
					</StyledPostBox.InfoHeading>
						<div>
							{`
								${nearestLocation.address.street1},
							${nearestLocation.address.postalCode}
							${nearestLocation.address.city}
						`}
						</div>
						<StyledPostBox.LocationLink
							href={mapUrls.link}
							target="_blank">
							<IconLocation />
							<span>{t('misc.see-on-map')}</span>
					</StyledPostBox.LocationLink>
					<StyledPostBox.InfoHeading marginBottom="1rem">{t('misc.opening-hours')}</StyledPostBox.InfoHeading>
					<div>{nearestLocation.statusMessage}</div>
					<StyledPostBox.ButtonWrapper>
						<A to={getDestinationUrl(nearestLocation.locationType)}>
							<Button primary>{getButtonText(nearestLocation.locationType)}</Button>
						</A>
					</StyledPostBox.ButtonWrapper>
				</Column>
				<Column tablet={4 / 12}>
						<StyledPostBox.Map>
							<iframe
								title={mapUrls.title}
								width="100%"
								height="100%"
								frameBorder="0"
								src={mapUrls.embed}
							/>
						</StyledPostBox.Map>
				</Column>
			</Columns>
		</StyledPostBox.Wrapper>
	);
};

export default Location;
